'use client';

import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useUser } from '@auth0/nextjs-auth0';
import { User } from '@/types/user';

interface AuthContextProps {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  error: Error | null;
}

const AuthContext = createContext<AuthContextProps>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user: auth0User, error: auth0Error, isLoading } = useUser();
  
  const [user, setUser] = useState<User | null>(null);
  
  // Transform Auth0 user data into our application's user model
  useEffect(() => {

    if (auth0User && !isLoading) {
      const appUser: User = {
        id: auth0User.sub as string,
        name: auth0User.name || auth0User.nickname || '',
        email: auth0User.email || '',
        profileImage: auth0User.picture || '',
        joinDate: new Date().toISOString(),
        preferences: {
          darkMode: true,
          emailNotifications: true,
          pushNotifications: false,
        },
      };
      console.log('Transformed user data:', appUser); 
      setUser(appUser);
    } else {
      console.log('No user data available');
      setUser(null);
    }
  }, [auth0User, isLoading]);

  const value = {
    isAuthenticated: !!user,
    isLoading,
    user,
    error: auth0Error as Error | null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext); 